import React, {useContext, useEffect, useRef, useState} from 'react';
import {CitizenContext} from "./context/citizen-context.jsx";
import CitizenTabs from "./components/citizen-tabs.jsx";
import {Card} from "../../../../../components/card/card.jsx";
import Button from "../../../../../components/button/button.jsx";
import citizenService from "../../../../../services/web/citizen-service.js";
import projectUtils from "../../../../../utils/project-utils.js";
import citizenAlertMessages from "../utils/citizen-alert-messages.js";
import {Template} from "../../../../../components/template/template.jsx";
import FormBuilderModal from "../../../form-builder/modal/form-builder-modal.jsx";
import {Modal} from "../../../../../components/modal/modal.jsx";
import InputTextarea from "../../../../../components/input/input-textarea.jsx";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import {FaCopy, FaHistory, FaPrint} from "react-icons/fa";
import CitizenResume from "./components/citizen-resume.jsx";
import CitizenGeneral from "./components/citizen-general.jsx";
import CitizenRole from "./components/citizen-role.jsx";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import moment from "moment";
import CitizenAddress from "./components/citizen-address.jsx";
import CitizenContacts from "./components/citizen-contacts.jsx";
import CitizenVehicles from "./components/citizen-vehicles.jsx";
import CitizenAttaches from "./components/citizen-attaches.jsx";
import CitizenAggressor from "./components/citizen-aggressor.jsx";
import CitizenTypeModal from "./components/citizen-type-modal.jsx";
import HeaderTemplateButton from "../../../../../components/button/header-template-button.jsx";
import {FaFilePen} from "react-icons/fa6";
import {useInactiveTabAutofillProtection} from "../../../../../hooks/use-required-tab-errors.js";

const CitizenForm = () => {
    const {id, citizen, attaches, attachesDescription, aggressors, refetch, markSaved, isLoadingFetch, citizenStatus, files, onlyView, sosView, citizenManage, isDirty, tabs, isLoading, setIsLoading} = useContext(CitizenContext);

    const navigate = navigateHook();

    const [formModal, setFormModal] = useState({open: false, object: undefined});
    const [inactivatedModal, setInactivatedModal] = useState(false);
    const [inactivateState, setInactivateState] = useState({inactivationReason: "", inactivationReasonDescription: ""});
    const [headerSelected, setHeaderSelected] = useState(-1);

    const [tabSelected, setTabSelected] = useState(id ? "resume" : null);
    const [aggressorSelected, setAggressorSelected] = useState(null);

    const printRef = useRef(undefined);
    const scrollRef = useRef(undefined);

    useInactiveTabAutofillProtection(scrollRef, tabSelected);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [tabSelected, aggressorSelected]);

    const tabComponents = {
        resume: (
            <CitizenResume
                printRef={printRef}
                headerSelected={headerSelected}
            />
        ),
        general: <CitizenGeneral setTabSelected={setTabSelected}/>,
        role: <CitizenRole setTabSelected={setTabSelected}/>,
        address: <CitizenAddress setTabSelected={setTabSelected}/>,
        contacts: <CitizenContacts setTabSelected={setTabSelected}/>,
        vehicles: <CitizenVehicles setTabSelected={setTabSelected}/>,
        attaches: <CitizenAttaches setTabSelected={setTabSelected}/>,
        aggressor: (
            <CitizenAggressor
                aggressorSelected={aggressorSelected}
                setAggressorSelected={setAggressorSelected}
                setTabSelected={setTabSelected}
            />
        ),
    };

    const tabKeys = [...new Set(tabs
        .flatMap(({key, children}) => [key, ...(children?.map(({key: childKey}) => childKey) ?? [])])
        .filter(Boolean)
        .concat(citizen.protectiveMeasure ? ["aggressor"] : []))];

    const submitAction = () => {
        if (citizenStatus.current === "ACTIVATED" && citizen.status === "INACTIVATED") {
            setInactivatedModal(true);
            setInactivateState({inactivationReason: "", inactivationReasonDescription: ""});
            return;
        }

        submit();
    }

    const inactivatedSubmit = () => {
        const {inactivationReason, inactivationReasonDescription} = inactivateState

        if (!inactivationReason) {
            projectUtils.swalErrorMessage('', citizenAlertMessages['missing.inactivationReason'], 'Ok');
            return;
        }
        if (!inactivationReason) {
            projectUtils.swalErrorMessage('', citizenAlertMessages['missing.inactivationDescription'], 'Ok');
            return;
        }

        submit(inactivationReason, inactivationReasonDescription);
        setInactivatedModal(false);
    }

    const submit = (inactivationReason = null, inactivationReasonDescription = null, ignoreAggressorWithoutCpf = false) => {
        const formData = new FormData();

        const preparedAttaches = attaches.map(a => {
            const ext = a.name.split('.').pop().toLowerCase();
            const fileName = a.new ? `sos-${a.attachId}.${ext}` : a.name;
            if (a.new) formData.append("files[]", a, fileName);

            return { attachId: a.attachId, extension: ext, name: fileName, size: a.size, description: attachesDescription[a.attachId] ?? null }
        });

        const attachWithoutDescription = files.find(a => !attachesDescription[a.attachId]);
        if (attachWithoutDescription) {
            projectUtils.swalErrorMessage("", "Insira uma descrição para o anexo \"" + attachWithoutDescription.name + "\".");
            return;
        }

        const preparedAggressors = citizen.protectiveMeasure ? aggressors.map((a, index) => {
            let currentId = a.itemId;
            const itemIdIsEmpty = (currentId === undefined || currentId === null);

            if (itemIdIsEmpty) {
                if (index === 0) {
                    currentId = 0;
                } else {
                    currentId = parseInt(aggressors[index - 1].itemId) + 1;
                }
            }

            return (
                {
                    ...a,
                    itemId: currentId,
                    cpf: a.cpf?.replace(/\D/g, ''),
                    violenceTypes: a.violenceTypes.map(v => v.value),
                    subpoena: a.subpoena?.value ?? null,
                    restrictions: a.restrictions?.map(r => r.value) ?? [],
                    dtBirth: a.dtBirth ? moment(a.dtBirth).format("YYYY-MM-DD") : null,
                    dtExpedition: a.dtExpedition ? moment(a.dtExpedition).format("YYYY-MM-DD") : null,
                    dtExpiration: a.dtExpiration ? moment(a.dtExpiration).format("YYYY-MM-DD") : null
                }
            )
        }) : [];

        const preparedAddresses = citizen.addresses?.map(a => {
            if (!a.placeId) return a;

            return {
                ...a,
                latitude: null,
                longitude: null,
                addressType: null,
                address: null,
                number: null,
                neighborhood: null,
                complement: null,
                city: null,
                zipCode: null,
                description: null,
                sector: null,
                attachId: null
            };
        });

        const aggressorsWithoutCpf = preparedAggressors.filter(a=> !a.cpf);
        if (aggressorsWithoutCpf.length && !ignoreAggressorWithoutCpf) {
            const aggressorsName = aggressorsWithoutCpf.map((a, i) => a?.name || "Agressor " + (i + 1));

            const multiple = aggressorsName.length > 1;
            const names = multiple
                ? `${aggressorsName.slice(0, -1).join(", ")} e ${aggressorsName.at(-1)}`
                : aggressorsName[0];

            const message = multiple
                ? `Os agressores ${names} não possuem CPF. Deseja continuar?`
                : `O agressor ${names} não possui CPF. Deseja continuar?`;

            projectUtils.swalMessage(
                "warning",
                "",
                message,
                "Sim",
                true,
                "Não",
                ({isConfirmed}) => {
                    if (isConfirmed) submit(inactivationReason, inactivationReasonDescription, true)
                }
            );
            return
        }

        const form = {
            ...citizen,
            addresses: preparedAddresses,
            attaches: preparedAttaches,
            aggressors: preparedAggressors,
            cpf: citizen.cpf?.replace(/\D/g, ''),
            cnpj: citizen.cnpj?.replace(/[^a-zA-Z0-9]/g, ''),
            dtBirth: citizen.dtBirth ? moment(citizen.dtBirth).format("YYYY-MM-DD") : null,
            protectionProgram: citizen.protectionProgram?.map(p => p.value)
        }

        if (inactivationReason && inactivationReasonDescription) {
            form.inactivationReason = inactivationReason;
            form.inactivationReasonDescription = inactivationReasonDescription;
        }

        const formJson= JSON.stringify(projectUtils.replaceEmptyStringsWithNull(form));
        const formBlob = new Blob([formJson], {
            type: 'application/json'
        });

        formData.append('citizen', formBlob);

        setIsLoading(true);

        let response;
        if (id) response = citizenService.edit(formData);
        else response = citizenService.create(formData);

        response
            .then((data) => {
                projectUtils.success(!id)

                if (id) {
                    refetch()
                    setTabSelected("resume")
                    return;
                }

                navigate(`/sos/citizens/form/${data}`);
            })
            .catch(error => {
                const response = error.response;

                let defaultSwalMessage = 'Não foi possível salvar.';
                let swalMessage = '';

                if (response && response.status === 400) {
                    const errorMessage = error.response.data.message;

                    if (errorMessage && typeof errorMessage == 'string') {
                        swalMessage = '';
                        const message = errorMessage.split(',')[0];
                        swalMessage = citizenAlertMessages[message.trim()] ?? message.trim();
                    }
                }

                if (!swalMessage) {
                    swalMessage = defaultSwalMessage;
                }

                projectUtils.swalErrorMessage('', swalMessage, 'Ok');
            })
            .finally(() => setIsLoading(false));
    }

    const openFormModal = () => {
        const object = {
            ...citizen,
            attaches,
            aggressors
        }

        setFormModal({open: true, object});
    }

    return (
        <>
            <Template isLoading={isLoadingFetch || isLoading}>
                <Template.Header>
                    <Template.Title browserTitle={'SOS Cadastro'} className={"flex gap-2 items-center"}>
                        {
                            id &&
                            <span className={"cursor-pointer text-[16px]"} title={"Copiar ID"} onClick={() => projectUtils.copyToClipboard(id, 'ID do protegido copiado.')}>
                                <FaCopy />
                            </span>
                        }
                        SOS Cadastro {id ? "- " + id : ""}
                    </Template.Title>

                    <div className={"flex flex-row gap-2"}>
                        {
                            id && citizen.protectiveMeasure && !onlyView &&
                            <Button title={"Formulários"} onClick={openFormModal}><FaFilePen /></Button>
                        }

                        {
                            id &&
                            <Button onClick={() => navigate(`/sos/citizen/history/${id}`, true)}>
                                <FaHistory /> Histórico
                            </Button>
                        }

                        {
                            id && tabSelected === "resume" &&
                                <Button title="Imprimir" onClick={() => printRef?.current.print()}>
                                    <FaPrint />
                                </Button>
                        }

                        <HeaderTemplateButton
                            selected={headerSelected}
                            setSelected={setHeaderSelected}
                        />
                    </div>
                </Template.Header>

                <Template.Body>
                    <div className={"grid grid-cols-12 gap-2"}>
                        {
                            !onlyView &&
                            <CitizenTabs
                                isLoadingFetch={isLoadingFetch}
                                tabSelected={tabSelected}
                                setTabSelected={setTabSelected}
                                aggressorSelected={aggressorSelected}
                                setAggressorSelected={setAggressorSelected}
                            />
                        }

                        <div className={"grid grid-cols-12 gap-x-2 " + (onlyView ? "col-span-12" : "col-span-10")}>
                            <div ref={scrollRef} className={"col-span-full overflow-auto " + (onlyView ? "h-[calc(100vh-70px)]" : "h-[calc(100vh-131px)]")}>
                                {tabKeys.map((key) => (
                                    <div
                                        key={key}
                                        data-required-tab={key}
                                        hidden={tabSelected !== key}
                                        aria-hidden={tabSelected !== key}
                                    >
                                        {tabComponents[key]}
                                    </div>
                                ))}
                            </div>

                            {
                                !onlyView &&
                                <div className="col-span-full">
                                    <Card className="mt-2">
                                        <Card.Body className={"flex gap-2 justify-end"}>
                                            <Button target={"primary"} onClick={submitAction} disabled={!isDirty || (sosView && !citizenManage)} actionFeedbackType="save" onActionFeedbackSuccess={markSaved}>
                                                Salvar
                                            </Button>
                                        </Card.Body>
                                    </Card>
                                </div>
                            }
                        </div>
                    </div>
                </Template.Body>
            </Template>

            <FormBuilderModal
                module={"SOS_MARIA_DA_PENHA"}
                object={formModal.object}
                open={formModal.open}
                externalId={citizen.id}
                setOpen={(open) => setFormModal(prev => ({...prev, open}))}
                setIsLoading={setIsLoading}
            />

            <CitizenTypeModal
                tabSelected={tabSelected}
                setTabSelected={setTabSelected}
            />

            <Modal open={inactivatedModal} setOpen={setInactivatedModal} title={"Inativar protegido"} className={"w-2xl"}>
                <Modal.Body className={"space-y-4 p-5 !mt-0"}>
                    <SelectComponent
                        value={inactivateState.inactivationReason}
                        label={"Motivo da inativação *"}
                        onChange={(value) => setInactivateState(prev => ({...prev, inactivationReason: value?.value}))}
                        options={[
                            {value: "INACTIVATION", label: "Inativação - Outros"},
                            {value: "INACTIVATION_REVOCATION", label: "Inativação - Revogação"},
                            {value: "INACTIVATION_REFUSAL", label: "Inativação - Recusa"},
                            {value: "INACTIVATION_TERMINATION", label: "Inativação - Desligamento"}
                        ]}
                    />

                    <InputTextarea
                        id={"inactivationReasonDescription"}
                        value={inactivateState.inactivationReasonDescription}
                        label={"Descrição da inativação * (esta mensagem não será visível para o protegido)"}
                        textAreaClass={"h-[100px]"}
                        onChange={({target: {value}}) => setInactivateState(prev => ({...prev, inactivationReasonDescription: value}))}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button target={"primary"} onClick={inactivatedSubmit} actionFeedbackType="save" onActionFeedbackSuccess={markSaved}>
                        Salvar
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CitizenForm;